import { Component, computed, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Cart, PaymentCart } from '@cargos/sprintpay-models';
import { Banner } from '@cargos/sprintpay-ui';
import {
    catchError,
    combineLatest,
    forkJoin,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    take,
    takeUntil,
    throwError,
} from 'rxjs';
import Swal from 'sweetalert2';
import { NotificationsType } from '../../../models/notifications';
import {
    CartBillService,
    ConfigService,
    CurrentFacilityService,
    PaymentMethodsService,
    SessionService,
} from '../../../services';
import { BannersService } from '../../../services/banners.service';
import { FeatureRestrictionsService } from '../../../services/features/feature-restrictions.service';
import { PromoCodesService } from '../../../services/promo-code.service';
import { PaymentMethodSelected } from '../lib-cart/balance/shared/models';
import { FeesConfiguration } from '../lib-cart/shared/models/convenience-fee.enum';
import { PaymentMethodsHandlerService } from '../lib-cart/shared/services/payment-methods-handler.service';

@Component({
    selector: 'app-cart',
    templateUrl: './cart.component.html',
    styleUrls: ['./cart.component.scss'],
    standalone: false,
})
export class MainCartComponent implements OnInit, OnDestroy {
    private displayModal = false;
    private unsubscribe$: Subject<void>;
    public cartBill: Cart;
    public isCartLoading: boolean;
    public showBreadcrumb: boolean;
    public chargesURL: string;
    public isAuthenticated: boolean;
    public helpInformation = 'Your own personal reference # so you can remember this payment. For example your file #.';
    public isPromoCodeEnable = false;
    public thresholdMessage: string = '';
    public isAccountOnHold = false;
    public collectCompanyInformationByPayment: boolean;
    public subtotal: number | null = null;
    public hasBelowThreshold: boolean = false;
    public hasAboveThreshold: boolean = false;
    public isAllowedMultipleAWBs: boolean = false;
    public cartNoteWithCheckbox: boolean = false;
    public termsOfUseURL: string = '';
    public lendingTerms: number = 0;
    public isAutomatedDebitsEnabled = false;
    public underLimitDisclaimer: string = '';
    public overLimitDisclaimer: string = '';
    public cartBillSignal = signal<Cart | null>(null);
    public cartHasMessageSignal = computed(() => !!this.cartBillSignal()?.threshold?.message);
    public banner: Banner;

    @Input() convenienceFees: FeesConfiguration[];
    @Input() cartNote: string = '';
    @Input() titleCustomFees: string;
    @Input() customSubtotal: boolean;
    @Input() customSubtotalTitle: string;
    @Input() emptyCartMessage: string;

    constructor(
        private cartBillService: CartBillService,
        private configService: ConfigService,
        private sessionService: SessionService,
        private promoCodeService: PromoCodesService,
        private featureRestrictionsService: FeatureRestrictionsService,
        private bannersService: BannersService,
        private router: Router,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService,
        private currentFacilityService: CurrentFacilityService,
        private paymentMethodsService: PaymentMethodsService
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.getConfiguration();
        this.subscribePaymentMethods();
        this.subscribeToCart();
        this.subscribeIsCartLoading();
        this.subscribeItemsDuplicates();
        this.subscribeUserAuthenticated();
        this.subscribeIfIsAccountOnHold();
        this.subscribeBanners();
        this.getTermsOfUseUrl();
        if (this.isPromoCodeEnable) {
            this.subscribeIfIsNecessaryPromoCode();
        }
        this.subscribeDisclaimers();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    getConfiguration(): void {
        this.collectCompanyInformationByPayment = this.configService.collectCompanyInformationByPayment();
        this.showBreadcrumb = this.configService.showBreadcrumbs();
        this.chargesURL = this.configService.getChargesURL();
        this.isPromoCodeEnable = this.configService.isPromoCodeEnable();
        this.cartNoteWithCheckbox = this.configService.isCompanyPolicyRequired();
        this.isAllowedMultipleAWBs = this.configService.isAllowedMultipleAWBs();
    }

    initBanner(): void {
        this.banner = {
            id: 'threshold_banner',
            message: this.thresholdMessage,
            icon: 'info',
            display: true,
            styleClass: 'info',
        };
    }

    subscribeToCart(): void {
        combineLatest([
            this.cartBillService.isCartAboveThreshold$(),
            this.cartBillService.isCartBelowThreshold$(),
            this.cartBillService.getCartBill$().pipe(
                switchMap((cart) => {
                    if (this.cartBill?.cartBillResponseDTOList?.length && this.isPromoCodeEnable) {
                        return this.validatePromoCodes(cart?.getCartPaymentRequests()).pipe(map(() => cart));
                    }

                    return of(cart);
                }),
                takeUntil(this.unsubscribe$)
            ),
            this.cartBillService.getMessageThreshold(),
        ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(([hasAboveThreshold, hasBelowThreshold, cart, threshold]) => {
                this.hasAboveThreshold = hasAboveThreshold;
                this.hasBelowThreshold = hasBelowThreshold;
                this.subtotal = (cart.subTotal || 0) + (cart.threshold?.subTotal || 0);
                this.cartBill = cart;
                this.cartBillSignal.set(cart);
                this.thresholdMessage = threshold?.message || '';
                if (this.cartHasMessageSignal()) {
                    this.initBanner();
                }
            });
    }

    validatePromoCodes(cart: PaymentCart[]): Observable<string> {
        return this.promoCodeService.getAllPromoCodeApplied$().pipe(
            map((promoCodes) => {
                promoCodes.forEach((promoCode) => {
                    const isOnCart = !!cart.find((paymentCart) => {
                        return paymentCart.awb === promoCode.awb && paymentCart?.paymentType?.toLowerCase() === 'isc';
                    });

                    if (!isOnCart) {
                        this.promoCodeService.removePromoCode(promoCode.awb, promoCode.promoCodeId);
                    }
                });

                return 'Complete';
            })
        );
    }

    subscribeIsCartLoading(): void {
        this.getIsCartLoading()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isLoading) => {
                    this.isCartLoading = isLoading;
                },
            });
    }

    getIsCartLoading(): Observable<boolean> {
        return this.cartBillService.getIsCartLoading$();
    }

    subscribeItemsDuplicates(): void {
        this.cartBillService
            .isThereDuplicates$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isDuplicated) => {
                if (isDuplicated && !this.displayModal) {
                    this.displayModal = true;
                    this.showModalItemsDuplicates();
                }
            });
    }

    showModalItemsDuplicates(): void {
        Swal.fire({
            title: 'Error!',
            text: `You have duplicate items in your cart. Remove one of the duplicates before continuing to pay.`,
            icon: 'error',
            confirmButtonText: 'Continue',
            showConfirmButton: true,
            showCloseButton: true,
        });
    }

    subscribeUserAuthenticated(): void {
        this.sessionService
            .isAuthenticated$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isAuthenticated) => {
                this.isAuthenticated = isAuthenticated;
            });
    }

    subscribeIfIsNecessaryPromoCode(): void {
        this.promoCodeService
            .validateIfIsNeccesaryPromoCode()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isNecessary: boolean) => {
                    this.promoCodeService.setIsPromoCodeNecessary(isNecessary);
                },
            });
    }

    subscribeIfIsAccountOnHold(): void {
        this.featureRestrictionsService
            .isAccountOnHold()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isAccountOnHold) => {
                    this.isAccountOnHold = isAccountOnHold;
                },
            });
    }

    private subscribeBanners(): void {
        this.bannersService
            .getCustomerNotificationsRequest()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((banners: NotificationsType[]) => {
                this.bannersService.setNotifications(banners);
            });
    }

    backToLogin(): void {
        this.router.navigate(['/login']);
    }

    getTermsOfUseUrl(): void {
        this.termsOfUseURL = this.currentFacilityService.getTermsOfUseURL();
    }

    subscribeDisclaimers(): void {
        combineLatest([
            this.paymentMethodsHandlerService.getPaymentMethodSelectedComplete$(false),
            this.paymentMethodsHandlerService.getPaymentMethodSelectedComplete$(true),
        ])
            .pipe(
                switchMap(([underMethod, overMethod]) => {
                    const under = underMethod?.name || '';
                    const over = overMethod?.name || '';

                    return combineLatest([
                        this.featureRestrictionsService.hasFinanceDisclaimer(under, false),
                        this.featureRestrictionsService.hasFinanceDisclaimer(over, true),
                    ]);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(([underDisclaimer, overDisclaimer]) => {
                this.underLimitDisclaimer = underDisclaimer;
                this.overLimitDisclaimer = overDisclaimer;
            });
    }

    subscribePaymentMethods(): void {
        forkJoin([
            this.paymentMethodsHandlerService.getEventsPaymentMethods(),
            this.getPaymentMethodsAndSetByDefaultIfNeeded(),
        ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe();
    }

    getPaymentMethodsAndSetByDefaultIfNeeded(): Observable<string> {
        this.paymentMethodsHandlerService.setSettingDefaultPaymentMethod(true);
        return this.paymentMethodsService.getPaymentMethodsRequest$().pipe(
            take(1),
            switchMap(() => {
                if (!this.isAuthenticated) {
                    this.paymentMethodsHandlerService.setSettingDefaultPaymentMethod(false);
                    this.paymentMethodsHandlerService.setPaymentMethodSelected(null);

                    return of('Complete');
                }

                return this.paymentMethodsHandlerService.getPaymentMethodByDefault().pipe(
                    map((paymentMethodByDefault: PaymentMethodSelected | null) => {
                        this.paymentMethodsHandlerService.setSettingDefaultPaymentMethod(false);
                        if (paymentMethodByDefault && this.cartBillService.isCartAboveThreshold()) {
                            if (
                                this.paymentMethodsService.isPaymentMethodsSelectedOnCartAboveValid(
                                    paymentMethodByDefault
                                )
                            ) {
                                this.paymentMethodsHandlerService.setPaymentMethodSelected(paymentMethodByDefault);
                            }

                            return 'Complete';
                        }

                        if (paymentMethodByDefault) {
                            this.paymentMethodsHandlerService.setPaymentMethodSelected(paymentMethodByDefault);
                        }

                        return 'Complete';
                    })
                );
            }),
            catchError((error) => {
                this.paymentMethodsHandlerService.setSettingDefaultPaymentMethod(false);
                return throwError(() => error);
            })
        );
    }
}
